import { useEffect, useState, useCallback } from "react";
import SEO from "@/components/SEO";
import {
  adminLogin,
  adminGetStats,
  adminGetLeads,
  adminGetBookings,
  adminGetPeptideInquiries,
  adminGetNewsletter,
  adminGetPayments,
  getAdminToken,
  setAdminToken,
  clearAdminToken,
} from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  Users,
  CalendarCheck,
  FlaskConical,
  Mail,
  RefreshCw,
  Shield,
  ArrowLeft,
  CreditCard,
} from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "@/data/content";

const TABS = [
  { id: "leads", label: "Leads", icon: Users },
  { id: "bookings", label: "Bookings", icon: CalendarCheck },
  { id: "peptides", label: "Peptide Inquiries", icon: FlaskConical },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "payments", label: "Payments", icon: CreditCard },
];

export default function Admin() {
  const [auth, setAuth] = useState(Boolean(getAdminToken()));
  const [pass, setPass] = useState("");
  const [logging, setLogging] = useState(false);

  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({
    leads: [],
    bookings: [],
    peptides: [],
    newsletter: [],
    payments: [],
  });
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, b, p, n, pay] = await Promise.all([
        adminGetStats(),
        adminGetLeads(),
        adminGetBookings(),
        adminGetPeptideInquiries(),
        adminGetNewsletter(),
        adminGetPayments(),
      ]);
      setStats(s);
      setData({ leads: l, bookings: b, peptides: p, newsletter: n, payments: pay });
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminToken();
        setAuth(false);
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load admin data.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth) loadAll();
  }, [auth, loadAll]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!pass) return;
    setLogging(true);
    try {
      await adminLogin(pass);
      setAdminToken(pass);
      setAuth(true);
      toast.success("Welcome, Coach J.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Invalid passcode."
      );
    } finally {
      setLogging(false);
    }
  };

  const onLogout = () => {
    clearAdminToken();
    setAuth(false);
    setPass("");
  };

  if (!auth) {
    return (
      <div className="min-h-screen bg-ng-bg hex-bg flex items-center justify-center px-6">
        <SEO title="Admin" robots="noindex, nofollow" />
        <div className="w-full max-w-md border border-ng-line bg-ng-surface p-10 shadow-[0_0_80px_-20px_rgba(30,144,255,0.5)]">
          <div className="flex items-center gap-3 mb-6">
            <Shield size={20} className="text-ng-blue" />
            <div className="label-eyebrow">Admin Access</div>
          </div>
          <h1 className="font-display font-bold uppercase text-3xl text-white tracking-tight">
            <span className="chrome-text">NEXT</span>
            <span className="electric-text">GEN</span>{" "}
            <span className="text-white/70 text-base ml-2 tracking-[0.25em] uppercase">
              Dashboard
            </span>
          </h1>
          <p className="mt-3 text-white/60 text-sm font-light">
            Private console for Coach J. Enter your passcode to continue.
          </p>

          <form onSubmit={onLogin} className="mt-8 space-y-5">
            <div>
              <label className="label-eyebrow block mb-2">Passcode</label>
              <input
                type="password"
                data-testid="admin-passcode"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                autoFocus
                className="w-full bg-transparent border-b border-ng-line text-white py-3 focus:outline-none focus:border-ng-blue transition-colors"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={logging}
              className="w-full bg-ng-blue text-white px-6 py-4 uppercase tracking-[0.22em] text-xs font-bold font-display hover:bg-ng-blueLight transition-colors flex items-center justify-center gap-3 disabled:opacity-60 shadow-[0_0_40px_-8px_rgba(30,144,255,0.7)]"
            >
              {logging ? <Loader2 size={16} className="animate-spin" /> : "Unlock Dashboard"}
            </button>
          </form>

          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-white/40 hover:text-ng-blue text-xs uppercase tracking-widest font-display transition-colors"
          >
            <ArrowLeft size={12} /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  const renderTable = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-ng-blue" />
        </div>
      );
    }
    if (tab === "leads") return <LeadsTable rows={data.leads} />;
    if (tab === "bookings") return <BookingsTable rows={data.bookings} />;
    if (tab === "peptides") return <PeptidesTable rows={data.peptides} />;
    if (tab === "newsletter") return <NewsletterTable rows={data.newsletter} />;
    if (tab === "payments") return <PaymentsTable rows={data.payments} />;
    return null;
  };

  return (
    <div className="min-h-screen bg-ng-bg hex-bg text-white">
      <SEO title="Admin Dashboard" robots="noindex, nofollow" />
      {/* Header */}
      <header className="border-b border-ng-line bg-ng-bg/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display italic font-black text-2xl tracking-tight leading-none flex">
                <span className="chrome-text">NEXT</span>
                <span className="electric-text">GEN</span>
              </span>
            </Link>
            <span className="hidden md:inline-block label-eyebrow border-l border-ng-line pl-3">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadAll}
              data-testid="admin-refresh"
              className="border border-ng-line hover:border-ng-blue text-white/70 hover:text-ng-blue p-2.5 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="border border-ng-line hover:border-red-500/60 text-white/70 hover:text-red-400 px-4 py-2 text-xs uppercase tracking-widest font-display font-bold transition-colors flex items-center gap-2"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10">
        <div className="label-eyebrow mb-2">Welcome back</div>
        <h1 className="font-display font-bold uppercase text-3xl md:text-4xl tracking-tight">
          Coach J&rsquo;s console
        </h1>
        <p className="text-white/50 text-sm mt-1">{BRAND.email}</p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard
            icon={<Users size={18} />}
            label="Leads"
            value={stats?.leads ?? "—"}
            testId="stat-leads"
          />
          <StatCard
            icon={<CalendarCheck size={18} />}
            label="Bookings"
            value={stats?.bookings ?? "—"}
            testId="stat-bookings"
          />
          <StatCard
            icon={<FlaskConical size={18} />}
            label="Peptide Inquiries"
            value={stats?.peptide_inquiries ?? "—"}
            testId="stat-peptides"
          />
          <StatCard
            icon={<Mail size={18} />}
            label="Subscribers"
            value={stats?.newsletter ?? "—"}
            testId="stat-newsletter"
          />
          <StatCard
            icon={<CreditCard size={18} />}
            label="Paid"
            value={stats?.paid_payments ?? "—"}
            testId="stat-payments"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-10">
        <div className="flex flex-wrap gap-2 border-b border-ng-line">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold font-display transition-colors border-b-2 -mb-px ${
                  active
                    ? "text-ng-blue border-ng-blue"
                    : "text-white/60 border-transparent hover:text-white"
                }`}
              >
                <Icon size={14} />
                {t.label}
                <span className="text-white/40 text-[10px]">
                  {data[t.id === "peptides" ? "peptides" : t.id]?.length || 0}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-8 pb-20">{renderTable()}</div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="border border-ng-line bg-ng-surface p-5 hover:border-ng-blue/40 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className="text-ng-blue">{icon}</div>
      </div>
      <div className="mt-3 font-display font-bold text-3xl text-white">
        {value}
      </div>
    </div>
  );
}

function TableShell({ headers, rows, empty, testId, renderRow }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-ng-line bg-ng-surface p-10 text-center text-white/50 font-light text-sm">
        {empty}
      </div>
    );
  }
  return (
    <div data-testid={testId} className="border border-ng-line bg-ng-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-ng-line">
            {headers.map((h) => (
              <th
                key={h}
                className="text-left py-3 px-4 label-eyebrow whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows.map(renderRow)}</tbody>
      </table>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function LeadsTable({ rows }) {
  return (
    <TableShell
      testId="admin-table-leads"
      empty="No leads yet."
      headers={["Name", "Email", "Phone", "Interest", "Message", "When"]}
      rows={rows}
      renderRow={(r) => (
        <tr key={r.id} className="border-b border-ng-line/40 hover:bg-ng-blue/5">
          <td className="py-3 px-4 text-white font-medium">{r.name}</td>
          <td className="py-3 px-4 text-ng-blue">{r.email}</td>
          <td className="py-3 px-4 text-white/70">{r.phone || "—"}</td>
          <td className="py-3 px-4 text-white/70">{r.interest || "—"}</td>
          <td className="py-3 px-4 text-white/70 max-w-xs truncate" title={r.message || ""}>
            {r.message || "—"}
          </td>
          <td className="py-3 px-4 text-white/40 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
        </tr>
      )}
    />
  );
}

function BookingsTable({ rows }) {
  return (
    <TableShell
      testId="admin-table-bookings"
      empty="No bookings yet."
      headers={["Name", "Email", "Phone", "Package", "Preferred", "Goals", "When"]}
      rows={rows}
      renderRow={(r) => (
        <tr key={r.id} className="border-b border-ng-line/40 hover:bg-ng-blue/5">
          <td className="py-3 px-4 text-white font-medium">{r.name}</td>
          <td className="py-3 px-4 text-ng-blue">{r.email}</td>
          <td className="py-3 px-4 text-white/70">{r.phone || "—"}</td>
          <td className="py-3 px-4 font-display font-bold text-ng-blue uppercase tracking-wider text-xs">
            {r.program}
          </td>
          <td className="py-3 px-4 text-white/70">{r.preferred_date || "—"}</td>
          <td className="py-3 px-4 text-white/70 max-w-xs truncate" title={r.goals || ""}>
            {r.goals || "—"}
          </td>
          <td className="py-3 px-4 text-white/40 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
        </tr>
      )}
    />
  );
}

function PeptidesTable({ rows }) {
  return (
    <TableShell
      testId="admin-table-peptides"
      empty="No peptide inquiries yet."
      headers={["Name", "Email", "Phone", "Compound", "Purpose", "Notes", "When"]}
      rows={rows}
      renderRow={(r) => (
        <tr key={r.id} className="border-b border-ng-line/40 hover:bg-ng-blue/5">
          <td className="py-3 px-4 text-white font-medium">{r.name}</td>
          <td className="py-3 px-4 text-ng-blue">{r.email}</td>
          <td className="py-3 px-4 text-white/70">{r.phone || "—"}</td>
          <td className="py-3 px-4 font-display font-bold text-ng-blue uppercase tracking-wider text-xs">
            {r.peptide}
          </td>
          <td className="py-3 px-4 text-white/70">{r.experience_level || "—"}</td>
          <td className="py-3 px-4 text-white/70 max-w-xs truncate" title={r.notes || ""}>
            {r.notes || "—"}
          </td>
          <td className="py-3 px-4 text-white/40 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
        </tr>
      )}
    />
  );
}

function NewsletterTable({ rows }) {
  return (
    <TableShell
      testId="admin-table-newsletter"
      empty="No subscribers yet."
      headers={["Email", "When"]}
      rows={rows}
      renderRow={(r) => (
        <tr key={r.id} className="border-b border-ng-line/40 hover:bg-ng-blue/5">
          <td className="py-3 px-4 text-ng-blue">{r.email}</td>
          <td className="py-3 px-4 text-white/40 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
        </tr>
      )}
    />
  );
}

function PaymentsTable({ rows }) {
  const statusColor = (s) => {
    if (s === "paid") return "text-green-400";
    if (s === "unpaid") return "text-yellow-400";
    return "text-white/50";
  };
  return (
    <TableShell
      testId="admin-table-payments"
      empty="No payments yet."
      headers={["Name", "Email", "Package", "Amount", "Status", "Session", "When"]}
      rows={rows}
      renderRow={(r) => (
        <tr key={r.session_id} className="border-b border-ng-line/40 hover:bg-ng-blue/5">
          <td className="py-3 px-4 text-white font-medium">{r.name || "—"}</td>
          <td className="py-3 px-4 text-ng-blue">{r.email || "—"}</td>
          <td className="py-3 px-4 font-display font-bold uppercase tracking-wider text-xs text-ng-blue">
            {r.package_id}
          </td>
          <td className="py-3 px-4 text-white font-display font-bold">
            ${Number(r.amount).toFixed(2)} {r.currency?.toUpperCase()}
          </td>
          <td className={`py-3 px-4 font-display font-bold uppercase tracking-widest text-xs ${statusColor(r.payment_status)}`}>
            {r.payment_status}
          </td>
          <td className="py-3 px-4 text-white/40 text-xs font-mono truncate max-w-[160px]" title={r.session_id}>
            {r.session_id}
          </td>
          <td className="py-3 px-4 text-white/40 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
        </tr>
      )}
    />
  );
}
